import React, { useEffect, useMemo, useState } from 'react';
import { Download, Share2, ImageDown, FileText } from 'lucide-react';
import JanelaModal from '../../JanelaModal';
import { addDays } from '../../../constants';
import { detalheDoEvento, periodoPorExtenso } from '../../../utils/calendarioExportado';
import {
  desenharAgenda, baixar, compartilhar, podeCompartilhar,
} from '../../../utils/imagemDoCalendario';
import { montarResumoDeGestao } from '../../../utils/resumoDeGestao';
import { montarRelatorioHTML } from '../../../utils/relatorioPDF';
import { abrirJanelaDeImpressao, escreverImpressao } from '../../../utils/workflowPDF';
import { buscarGestao } from '../../../data/gestao';

// Mais que isso a imagem vira um pôster que o WhatsApp comprime até ninguém ler.
const MAXIMO_DE_SEMANAS = 6;

const diaDaSemana = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
};

/**
 * MANDAR O CALENDÁRIO PARA ALGUÉM.
 *
 * Duas saídas, duas perguntas. A AGENDA é imagem e responde "o que tem pela
 * frente" (e, nos dias que já passaram, o que saiu deles) — cabe no WhatsApp.
 * O RESUMO DE GESTÃO é PDF e responde "como foi o período": produção,
 * financeiro e equipe, cada um com o que foi feito e o que ficou em aberto.
 * É documento de várias páginas, e PDF é o formato de documento.
 *
 * As duas saem do mesmo detalhe que o cartão da tela mostra — o papel não pode
 * contar outra história.
 *
 * O período vem preenchido com o que está na tela, porque quase sempre é isso
 * que se quer mandar; mudar é o caso raro.
 */
const ExportarCalendario = ({
  eventos, servicos, clientes = [], pendencias = null, cobranca = [], rotuloDoEvento,
  laboratorio, laboratoryId, podeFinanceiro = false,
  hoje, deInicial, ateInicial, onFechar,
}) => {
  const [tipo, setTipo] = useState('agenda');
  const [de, setDe] = useState(deInicial);
  const [ate, setAte] = useState(ateInicial);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  const compartilha = useMemo(podeCompartilhar, []);

  const valido = !!de && !!ate && de <= ate;

  const semanas = useMemo(() => {
    if (!valido) return [];
    const inicio = addDays(de, -diaDaSemana(de));
    const out = [];
    for (let w = 0; w < MAXIMO_DE_SEMANAS; w++) {
      const dias = Array.from({ length: 7 }, (_, i) => addDays(inicio, 7 * w + i));
      if (dias[0] > ate) break;
      out.push(dias.map(d => (d >= de && d <= ate ? d : null)));
    }
    return out;
  }, [de, ate, valido]);

  const cortado = valido && semanas.length === MAXIMO_DE_SEMANAS
    && semanas[MAXIMO_DE_SEMANAS - 1][6] !== null && semanas[MAXIMO_DE_SEMANAS - 1][6] < ate;

  const titulo = valido ? `Agenda — ${periodoPorExtenso(de, ate)}` : '';

  const canvas = useMemo(() => {
    if (!valido || tipo !== 'agenda') return null;
    const eventosPorDia = {};
    semanas.flat().filter(Boolean).forEach(iso => {
      eventosPorDia[iso] = (eventos[iso] || []).map(ev => {
        const det = detalheDoEvento(ev);
        return {
          rotulo: rotuloDoEvento(ev.type),
          cliente: ev.service.client_name,
          lote: ev.service.service_label,
          detalhe: det?.texto,
          feito: !!det?.feito,
          prazo: !!ev.prazo,
          vencido: !!ev.prazo && iso < hoje,
        };
      });
    });
    return desenharAgenda({ semanas, eventosPorDia, hoje, laboratorio, titulo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, de, ate, valido, semanas, eventos]);

  const [previa, setPrevia] = useState(null);
  useEffect(() => { setPrevia(canvas ? canvas.toDataURL('image/png') : null); }, [canvas]);

  const nome = `agenda-${de}-a-${ate}.png`;

  const gerarPDF = async () => {
    // A janela abre AGORA, no clique; o relatório chega nela depois.
    const win = abrirJanelaDeImpressao();
    if (!win) return;
    setEnviando(true);
    setErro(null);
    try {
      const dados = await buscarGestao({ laboratoryId, de, ate, comDinheiro: podeFinanceiro });
      const spec = montarResumoDeGestao({
        de, ate, hoje, laboratorio, services: servicos, clients: clientes, dados, pendencias, cobranca,
        comDinheiro: podeFinanceiro,
      });
      escreverImpressao(win, spec.titulo, montarRelatorioHTML(spec));
    } catch (e) {
      win.close();
      setErro(`Não deu para montar o resumo: ${e?.message || e}`);
    } finally {
      setEnviando(false);
    }
  };

  const enviar = async (fn) => {
    if (!canvas) return;
    setEnviando(true);
    try { await fn(); } finally { setEnviando(false); }
  };

  return (
    <JanelaModal chave="exportar-calendario" titulo="Exportar calendário" Icone={ImageDown}
      onFechar={onFechar} largura="sm:max-w-4xl">
      <div className="p-4 space-y-3 flex-1 flex flex-col min-h-0">
        <div className="flex flex-wrap items-end gap-3">
          <div className="inline-flex rounded-md border border-slate-300 overflow-hidden">
            {[['agenda', 'Agenda (imagem)'], ['gestao', 'Resumo de gestão (PDF)']].map(([id, rotulo]) => (
              <button key={id} type="button" onClick={() => setTipo(id)} aria-pressed={tipo === id}
                className={`px-3 py-1.5 text-xs font-medium border-l first:border-l-0 border-slate-200 ${
                  tipo === id ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                {rotulo}
              </button>
            ))}
          </div>
          <label className="text-xs text-slate-600">
            De
            <input type="date" value={de} onChange={e => setDe(e.target.value)}
              className="block mt-0.5 px-2 py-1 text-sm border border-slate-300 rounded" />
          </label>
          <label className="text-xs text-slate-600">
            Até
            <input type="date" value={ate} onChange={e => setAte(e.target.value)}
              className="block mt-0.5 px-2 py-1 text-sm border border-slate-300 rounded" />
          </label>
        </div>

        <p className="text-xs text-slate-500">
          {tipo === 'agenda'
            ? 'Cada evento com o detalhe: o resultado no que já foi feito, o que vai ser trabalhado no que está agendado.'
            : `Resumo, produção${podeFinanceiro ? ', financeiro' : ''} e equipe — o que foi feito no período e o que está em aberto hoje.`}
          {!valido && ' A data final tem que ser depois da inicial.'}
          {cortado && tipo === 'agenda' && ` A agenda vai até ${MAXIMO_DE_SEMANAS} semanas; o resto fica de fora.`}
        </p>

        {tipo === 'agenda' && (
          <div className="flex-1 min-h-[12rem] overflow-auto border border-slate-200 rounded bg-slate-50">
            {previa
              ? <img src={previa} alt={titulo} className="block max-w-full h-auto mx-auto" />
              : <p className="text-sm text-slate-400 text-center py-10">Escolha o período.</p>}
          </div>
        )}

        {tipo === 'gestao' && (
          <ul className="text-sm text-slate-700 space-y-1.5 border border-slate-200 rounded p-3 bg-slate-50">
            <li><b>Resumo</b> — serviços e clientes criados, etapas concluídas{podeFinanceiro && ', faturado e recebido'}, o que está em aberto.</li>
            <li><b>Produção</b> — etapas concluídas com o resultado, etapas sem lançamento, os próximos 7 dias e a produção por cliente.</li>
            {podeFinanceiro && (
              <li><b>Financeiro</b> — faturas emitidas e recebimentos do período; vencido, a vencer e o que falta cobrar ou faturar.</li>
            )}
            <li><b>Equipe</b> — quantos registros cada pessoa criou, alterou e excluiu no sistema.</li>
          </ul>
        )}

        {erro && <p className="text-sm text-rose-700">{erro}</p>}

        <div className="flex justify-end gap-2">
          {tipo === 'gestao' && (
            <button type="button" disabled={!valido || enviando} onClick={gerarPDF}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white
                         bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
              <FileText size={14} /> {enviando ? 'Montando…' : 'Gerar PDF'}
            </button>
          )}
          {tipo === 'agenda' && compartilha && (
            <button type="button" disabled={!canvas || enviando}
              onClick={() => enviar(() => compartilhar(canvas, nome, titulo))}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700
                         bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50">
              <Share2 size={14} /> Compartilhar
            </button>
          )}
          {tipo === 'agenda' && (
          <button type="button" disabled={!canvas || enviando}
            onClick={() => enviar(() => baixar(canvas, nome))}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white
                       bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
            <Download size={14} /> Baixar PNG
          </button>
          )}
        </div>
      </div>
    </JanelaModal>
  );
};

export default ExportarCalendario;
